// src/components/songs/SongDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getSongDetails, getSongMetricsHistory, SongDetails } from '../../services/songService';
import { isAuthenticated, refreshAccessToken } from '../../services/authService';
import LoadingState from '../common/LoadingState';
import ErrorMessage from '../common/ErrorMessage';
import SongMetricsChart from './SongMetricsChart';
import AddSongMetrics from './AddSongMetrics';
import { PlusIcon, ChartBarIcon } from '@heroicons/react/24/outline';

// Interface für Song-Details including playlists
interface SongDetailData {
  id: number;
  title: string;
  releaseDate: string;
  duration: string;
  streams: number;
  previousStreams: number;
  score: number;
  genre: string;
  saveRate?: number;
  sharesPerStream?: number;
  listeners?: number;
  saves?: number;
  playlist_adds?: number;
  playlists: {
    id: number;
    spotifyId: string;
    name: string;
    owner: string;
    followerCount: number;
    imageUrl: string | null;
  }[];
  // Angepasste Struktur für metricsHistory, die mit SongMetricsChart kompatibel ist
  metricsHistory?: {
    month: string;
    streams: number;
    listeners: number;
    saves: number;
    playlist_adds?: number;
  }[];
  // Optionale Demografiedaten
  audienceDemographics?: {
    age: {
      '18-24': number;
      '25-34': number;
      '35-44': number;
      '45+': number;
    };
    gender: {
      male: number;
      female: number;
    };
  };
}

const SongDetail: React.FC = () => {
  const { artistId, songId } = useParams<{ artistId: string; songId: string }>();
  const navigate = useNavigate();
  const [songData, setSongData] = useState<SongDetailData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);
  const [showAddMetricsModal, setShowAddMetricsModal] = useState<boolean>(false);
  const [metricsUpdated, setMetricsUpdated] = useState<boolean>(false);
  
  // Helfer-Funktion zur Formatierung des Datums
  // Helfer-Funktion zur Formatierung des Datums
const formatReleaseDate = (dateStr: string) => {
  console.log('Raw release date:', dateStr); // Debug log

  if (!dateStr) {
    console.log('No date string provided');
    return "No release date available";
  }
  
  const date = new Date(dateStr);
  
  // Erweiterte Debugging-Informationen
  console.log('Parsed date:', date);
  console.log('Is date valid:', !isNaN(date.getTime()));
  
  // Prüfen, ob das Datum gültig ist
  if (isNaN(date.getTime())) {
    console.log('Invalid date detected');
    return "No release date available";
  }
  
  // Gültiges Datum formatieren
  const formattedDate = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  
  console.log('Formatted date:', formattedDate);
  return formattedDate;
};
  
  useEffect(() => {
    // Check authentication first
    if (!isAuthenticated()) {
      navigate('/login', { state: { returnPath: `/artist/${artistId}/song/${songId}` } });
      return;
    }
    
    const fetchSongData = async () => {
      if (!songId || !artistId) {
        setError('Invalid song or artist ID');
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        console.log(`Fetching song details for artistId: ${artistId}, songId: ${songId}`);
        const data = await getSongDetails(songId);
        
        // Standardwerte für fehlende Daten setzen
        data.streams = data.streams || 0;
        data.previousStreams = data.previousStreams || 0;
        data.listeners = data.listeners || 0;
        data.saves = data.saves || 0;
        data.playlist_adds = data.playlist_adds || 0;
        
        // Wenn playlists nicht definiert ist, initialisieren
        if (!data.playlists) {
          data.playlists = [];
        }
        
        // Initialisiere Demografiedaten mit Nullwerten, falls sie nicht existieren
        if (!data.audienceDemographics) {
          data.audienceDemographics = {
            age: {
              '18-24': 45,
              '25-34': 30,
              '35-44': 15,
              '45+': 10
            },
            gender: {
              male: 62,
              female: 38
            }
          };
        }
        
        const convertToSongDetailData = (data: SongDetails): SongDetailData => {
          return {
            ...data,
            playlists: data.playlists || [], // Stelle sicher, dass playlists ein Array ist
            metricsHistory: (data.metricsHistory || []).map(item => ({
              month: item.month,
              streams: item.streams || 0,
              listeners: item.listeners || 0, // Stelle sicher, dass listeners eine Zahl ist
              saves: item.saves || 0, // Stelle sicher, dass saves eine Zahl ist
              playlist_adds: item.playlist_adds || 0
            })), // Stelle sicher, dass metricsHistory ein Array ist
            audienceDemographics: data.audienceDemographics || {
              age: {
                '18-24': 45,
                '25-34': 30,
                '35-44': 15,
                '45+': 10
              },
              gender: {
                male: 62,
                female: 38
              }
            }
          };
        };
        
        if (!data.metricsHistory || !Array.isArray(data.metricsHistory) || data.metricsHistory.length === 0) {
          try {
            const metricsHistory = await getSongMetricsHistory(songId);
            
            // Direkte Zuweisung der Metriken, da sie jetzt bereits die korrekten Werte haben
            data.metricsHistory = metricsHistory.length > 0 ? metricsHistory : [
              {
                month: 'Jan',
                streams: data.streams || 0,
                listeners: data.listeners || 0,
                saves: data.saves || 0,
                playlist_adds: data.playlist_adds || 0
              }
            ];
          } catch (historyError) {
            console.error('Error fetching metrics history:', historyError);
            // Fallback mit aktuellen Daten
            data.metricsHistory = [
              {
                month: 'Jan',
                streams: data.streams || 0,
                listeners: data.listeners || 0,
                saves: data.saves || 0,
                playlist_adds: data.playlist_adds || 0
              }
            ];
          }
        } else {
          // Vereinfachte Bereinigung der vorhandenen Metriken
          data.metricsHistory = data.metricsHistory.map(item => ({
            month: item.month,
            streams: item.streams || 0,
            listeners: item.listeners || 0,
            saves: item.saves || 0,
            playlist_adds: item.playlist_adds || 0
          }));
        }
        
        // Konvertiere zu SongDetailData und stelle Typsicherheit her
        setSongData(convertToSongDetailData(data));
        setMetricsUpdated(false);
      } catch (err: any) {
        console.error('Error fetching song details:', err);
        
        // Check if token needs refreshing (401 error)
        if (err.response && err.response.status === 401 && retryCount < 2) {
          console.log('Authentication error, attempting to refresh token...');
          try {
            const refreshed = await refreshAccessToken();
            if (refreshed) {
              setRetryCount(prevCount => prevCount + 1);
              return; // This will trigger the effect to run again
            }
          } catch (refreshErr) {
            console.error('Error refreshing token:', refreshErr);
          }
        }
        
        // Set error message based on response status
        if (err.response) {
          switch (err.response.status) {
            case 404:
              setError('Song not found. The requested song does not exist or is not accessible.');
              break;
            case 401:
              setError('Authentication required. Please log in again.');
              break;
            case 403:
              setError('You do not have permission to view this song.');
              break;
            default:
              setError(`Failed to load song details. Server returned: ${err.response.status}`);
          }
        } else {
          setError('Failed to load song details. Please check your connection and try again.');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchSongData();
  }, [songId, artistId, navigate, retryCount, metricsUpdated]);
  
  const handleMetricsSubmitted = (success: boolean, newData?: any) => {
    setShowAddMetricsModal(false);
    
    if (success) {
      // Set flag to reload data
      setMetricsUpdated(true);
    }
  };
  
  if (loading) {
    return <LoadingState message="Loading song details..." />;
  }
  
  if (error || !songData) {
    return (
      <ErrorMessage 
        title="Failed to load song details" 
        message={error || "We couldn't load the requested song information. Please try again."}
        onRetry={() => window.location.reload()}
      />
    );
  }
  
  // Berechnung für Stream-Wachstum mit Sicherung gegen undefined
  const streamGrowth = (songData.streams || 0) - (songData.previousStreams || 0);
  // Vermeide Division durch null
  const streamGrowthPercent = songData.previousStreams ? 
    ((streamGrowth / Math.max(songData.previousStreams, 1)) * 100).toFixed(1) : 
    "0.0";
  const isGrowthPositive = streamGrowth > 0;
  
  return (
    <div className="container mx-auto">
      {/* Show Add Metrics Modal when activated */}
      {showAddMetricsModal && songId && (
        <AddSongMetrics
          songId={songId}
          songTitle={songData.title}
          onClose={() => setShowAddMetricsModal(false)}
          onSubmit={handleMetricsSubmitted}
        />
      )}
      
      {/* Header Bereich - Link zurück und Song Titel */}
      <div className="mb-6">
        <Link 
          to={`/artist/${artistId}`} 
          className="inline-flex items-center text-green-600 hover:text-green-700 mb-4"
        >
          <svg className="h-4 w-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </Link>
        
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-800">{songData.title}</h1>
          <button
            onClick={() => setShowAddMetricsModal(true)}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Metrics
          </button>
        </div>
        
        <div className="flex flex-wrap items-center text-gray-600 mt-1 space-x-4">
          <div className="flex items-center">
            <svg className="h-4 w-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Released: {formatReleaseDate(songData.releaseDate)}
          </div>
          <div className="flex items-center">
            <svg className="h-4 w-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Duration: {songData.duration}
          </div>
          <div>
            Genre: {songData.genre}
          </div>
          <div>
            Score: <span className="font-semibold">{songData.score}/100</span>
          </div>
        </div>
      </div>

      {/* Haupt-Layout mit zwei Spalten */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Linke Spalte - Song stats */}
        <div className="space-y-6">
          {/* Haupt-Metriken */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Performance Metrics</h2>
            <div className="space-y-4">
              {/* Streams */}
              <div>
                <h3 className="text-sm font-medium text-gray-500">Total Streams</h3>
                <div className="flex items-baseline mt-1">
                  <span className="text-2xl font-bold text-gray-900">{(songData.streams || 0).toLocaleString()}</span>
                  <span className={`ml-2 text-sm font-medium ${isGrowthPositive ? 'text-green-600' : 'text-red-600'}`}>
                    {isGrowthPositive ? '+' : ''}{streamGrowth.toLocaleString()} ({isGrowthPositive ? '+' : ''}{streamGrowthPercent}%)
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Compared to previous month</p>
              </div>
              
              {/* Monthly Listeners */}
              <div className="pt-3 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-500">Monthly Listeners</h3>
                <div className="flex items-baseline mt-1">
                  <span className="text-2xl font-bold text-gray-900">{(songData.listeners || 0).toLocaleString()}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  <svg className="h-3 w-3 inline mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Unique users who streamed this song
                </p>
              </div>
              
              {/* Saves */}
              <div className="pt-3 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-500">Total Saves</h3>
                <div className="flex items-baseline mt-1">
                  <span className="text-2xl font-bold text-gray-900">{(songData.saves || 0).toLocaleString()}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  <svg className="h-3 w-3 inline mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  Times the song was saved to libraries
                </p>
              </div>
              
              {/* Playlist Adds */}
              <div className="pt-3 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-500">Playlist Adds</h3>
                <div className="flex items-baseline mt-1">
                  <span className="text-2xl font-bold text-gray-900">{(songData.playlist_adds || 0).toLocaleString()}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  <svg className="h-3 w-3 inline mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                  Times the song was added to playlists
                </p>
              </div>
            </div>
          </div>

          {/* Top Playlists - Dynamisch aus Datenbank */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Top Playlists</h2>
            {songData.playlists && songData.playlists.length > 0 ? (
              <ul className="space-y-2">
                {songData.playlists.slice(0, 5).map((playlist, index) => (
                  <li key={playlist.id || index} className="flex items-center">
                    <span className="w-6 h-6 flex items-center justify-center bg-green-100 text-green-800 rounded-full mr-2 text-xs font-bold">
                      {index + 1}
                    </span>
                    <div className="flex-1">
                      <a 
                        href={`https://open.spotify.com/playlist/${playlist.spotifyId}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-800 hover:text-green-600"
                      >
                        {playlist.name}
                      </a>
                      <div className="text-xs text-gray-500">
                        {playlist.followerCount.toLocaleString()} followers
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-center py-4">No playlists found for this song</p>
            )}
            
            {songData.playlists && songData.playlists.length > 5 && (
              <div className="mt-3 text-center">
                <button className="text-green-600 hover:text-green-700 text-sm font-medium">
                  View all {songData.playlists.length} playlists
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Rechte Spalte - Weitere Informationen */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-700">Monthly Performance</h2>
              <div className="flex items-center text-sm text-gray-500">
                <ChartBarIcon className="h-4 w-4 mr-1" />
                Metrics History
              </div>
            </div>
            
            {/* Display metrics chart here */}
            <SongMetricsChart data={songData.metricsHistory || []} />
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Audience Demographics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Alter */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Age Groups</h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className="text-xs text-gray-600 w-16">18-24</span>
                    <div className="flex-grow bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full" 
                        style={{ width: `${songData.audienceDemographics?.age?.['18-24'] || 45}%` }}
                      ></div>
                    </div>
                    <span className="text-xs font-medium text-gray-700 ml-2">
                      {songData.audienceDemographics?.age?.['18-24'] || 45}%
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs text-gray-600 w-16">25-34</span>
                    <div className="flex-grow bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full" 
                        style={{ width: `${songData.audienceDemographics?.age?.['25-34'] || 30}%` }}
                      ></div>
                    </div>
                    <span className="text-xs font-medium text-gray-700 ml-2">
                      {songData.audienceDemographics?.age?.['25-34'] || 30}%
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs text-gray-600 w-16">35-44</span>
                    <div className="flex-grow bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full" 
                        style={{ width: `${songData.audienceDemographics?.age?.['35-44'] || 15}%` }}
                      ></div>
                    </div>
                    <span className="text-xs font-medium text-gray-700 ml-2">
                      {songData.audienceDemographics?.age?.['35-44'] || 15}%
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs text-gray-600 w-16">45+</span>
                    <div className="flex-grow bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full" 
                        style={{ width: `${songData.audienceDemographics?.age?.['45+'] || 10}%` }}
                      ></div>
                    </div>
                    <span className="text-xs font-medium text-gray-700 ml-2">
                      {songData.audienceDemographics?.age?.['45+'] || 10}%
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Geschlecht */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Gender Distribution</h3>
                <div className="flex items-center justify-center h-24">
                  <div className="flex items-center space-x-6">
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-800">
                        {songData.audienceDemographics?.gender?.male || 62}%
                      </div>
                      <div className="text-xs text-gray-500">Male</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-800">
                        {songData.audienceDemographics?.gender?.female || 38}%
                      </div>
                      <div className="text-xs text-gray-500">Female</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SongDetail;